/**
 * src/components/ComparisonChart.js
 * Comparison visualization — side-by-side funnels or overlaid time-series charts with deltas.
 */

import FunnelChart from './FunnelChart';
import TimeSeriesChart from './TimeSeriesChart';

function DeltaBadge({ value, suffix = '', invert = false }) {
  if (value == null) return <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>—</span>;
  const isPositive = invert ? value < 0 : value > 0;
  const isNegative = invert ? value > 0 : value < 0;
  const color = isPositive ? 'var(--success, #22c55e)' : isNegative ? 'var(--danger, #ef4444)' : 'var(--text-tertiary)';
  const arrow = value > 0 ? '↑' : value < 0 ? '↓' : '–';
  const display = value > 0 ? `+${value}` : `${value}`;
  return (
    <span style={{ fontSize: '11px', fontWeight: 600, color }}>
      {arrow} {display}{suffix}
    </span>
  );
}

function FunnelComparison({ scenarioA, scenarioB, deltas, compact }) {
  if (compact) {
    const delta = deltas?.completion_rate_delta;
    const rateB = scenarioB?.data?.overall_completion?.rate;
    return (
      <div style={{ padding: '12px', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: '12px', marginBottom: '4px' }}>
          <span style={{ fontSize: '20px', fontWeight: 700 }}>{rateB != null ? `${rateB}%` : '—'}</span>
          <DeltaBadge value={delta} suffix=" pp" />
        </div>
        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
          completion rate ({scenarioA?.label} → {scenarioB?.label})
        </div>
      </div>
    );
  }

  const stepsA = scenarioA?.data?.steps || [];
  const stepsB = scenarioB?.data?.steps || [];
  const stepDeltas = deltas?.steps || [];

  return (
    <div>
      {/* Scenario headers */}
      <div className="comparison-layout">
        <div className="comparison-scenario-header">
          <strong>{scenarioA?.label || 'Scenario A'}</strong>
          {scenarioA?.filters && (
            <span className="comparison-filter-summary">
              {[scenarioA.filters.from, scenarioA.filters.to].filter(Boolean).join(' – ')}
              {scenarioA.filters.config_version && ` · v${scenarioA.filters.config_version}`}
            </span>
          )}
        </div>
        <div style={{ minWidth: '80px' }} />
        <div className="comparison-scenario-header">
          <strong>{scenarioB?.label || 'Scenario B'}</strong>
          {scenarioB?.filters && (
            <span className="comparison-filter-summary">
              {[scenarioB.filters.from, scenarioB.filters.to].filter(Boolean).join(' – ')}
              {scenarioB.filters.config_version && ` · v${scenarioB.filters.config_version}`}
            </span>
          )}
        </div>
      </div>

      {/* Side-by-side funnels with deltas */}
      <div className="comparison-layout">
        <div style={{ flex: 1, minWidth: 0 }}>
          {stepsA.length > 0 ? <FunnelChart steps={stepsA} /> : <div className="comparison-no-data">No data</div>}
        </div>
        <div className="comparison-deltas-column">
          {stepDeltas.map((d, i) => (
            <div key={i} className="comparison-delta-cell">
              <DeltaBadge value={d.entity_count_pct_change} suffix="%" />
              {d.conversion_rate_delta != null && i > 0 && (
                <div style={{ marginTop: '2px' }}>
                  <DeltaBadge value={d.conversion_rate_delta} suffix=" pp" />
                </div>
              )}
            </div>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {stepsB.length > 0 ? <FunnelChart steps={stepsB} /> : <div className="comparison-no-data">No data</div>}
        </div>
      </div>

      {/* Overall completion delta */}
      <div className="comparison-overall">
        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Completion rate:</span>
        <span style={{ margin: '0 8px' }}>
          {scenarioA?.data?.overall_completion?.rate ?? '—'}% → {scenarioB?.data?.overall_completion?.rate ?? '—'}%
        </span>
        <DeltaBadge value={deltas?.completion_rate_delta} suffix=" pp" />
      </div>
    </div>
  );
}

function TimeSeriesComparison({ scenarioA, scenarioB, deltas, compact }) {
  if (compact) {
    const seriesDeltas = deltas?.series || [];
    return (
      <div style={{ padding: '8px 12px' }}>
        {seriesDeltas.slice(0, 3).map((s, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 0' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{s.label}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                {s.total_count_a} → {s.total_count_b}
              </span>
              <DeltaBadge value={s.total_pct_change} suffix="%" />
            </span>
          </div>
        ))}
      </div>
    );
  }

  // Overlay both scenarios' series on one chart with A=solid, B=dashed
  const dataA = scenarioA?.data?.series || [];
  const dataB = scenarioB?.data?.series || [];
  const granularity = scenarioA?.data?.granularity || scenarioB?.data?.granularity || 'day';
  const chartType = scenarioA?.data?.chart_type || scenarioB?.data?.chart_type || 'line';

  // Combine: label series as "Label (A)" and "Label (B)"
  const combinedSeries = [
    ...dataA.map(s => ({ ...s, label: `${s.label} (${scenarioA?.label || 'A'})` })),
    ...dataB.map(s => ({ ...s, label: `${s.label} (${scenarioB?.label || 'B'})` }))
  ];

  const seriesDeltas = deltas?.series || [];

  return (
    <div>
      {/* Scenario headers */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <div className="comparison-scenario-header">
          <strong>{scenarioA?.label || 'Scenario A'}</strong>
          {scenarioA?.filters && (
            <span className="comparison-filter-summary">
              {[scenarioA.filters.from, scenarioA.filters.to].filter(Boolean).join(' – ')}
              {scenarioA.filters.config_version && ` · v${scenarioA.filters.config_version}`}
            </span>
          )}
        </div>
        <div className="comparison-scenario-header">
          <strong>{scenarioB?.label || 'Scenario B'}</strong>
          {scenarioB?.filters && (
            <span className="comparison-filter-summary">
              {[scenarioB.filters.from, scenarioB.filters.to].filter(Boolean).join(' – ')}
              {scenarioB.filters.config_version && ` · v${scenarioB.filters.config_version}`}
            </span>
          )}
        </div>
      </div>

      {combinedSeries.length > 0
        ? <TimeSeriesChart data={combinedSeries} granularity={granularity} chartType={chartType} />
        : <div className="comparison-no-data">No data for either scenario</div>
      }

      {/* Series totals with deltas */}
      {seriesDeltas.length > 0 && (
        <div className="comparison-series-deltas">
          {seriesDeltas.map((s, i) => (
            <div key={i} className="comparison-series-delta-row">
              <span style={{ fontSize: '13px' }}>{s.label}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                  {s.total_count_a.toLocaleString()} → {s.total_count_b.toLocaleString()}
                </span>
                <DeltaBadge value={s.total_pct_change} suffix="%" />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ComparisonChart({ baseType, scenarioA, scenarioB, deltas, compact }) {
  if (baseType === 'funnel') {
    return <FunnelComparison scenarioA={scenarioA} scenarioB={scenarioB} deltas={deltas} compact={compact} />;
  }
  if (baseType === 'time_series') {
    return <TimeSeriesComparison scenarioA={scenarioA} scenarioB={scenarioB} deltas={deltas} compact={compact} />;
  }
  return <div style={{ padding: '12px', color: 'var(--text-tertiary)', fontSize: '13px' }}>Unsupported base type: {baseType}</div>;
}
